import React from 'react';
import { useTranslation } from 'react-i18next';
import './LotteryStatsDisplay.css';

/**
 * Reusable component for displaying lottery statistics
 * Handles rendering of combinations, numbers, and additional data
 *
 * @param {object} props - Component props
 * @param {array} props.combinations - Array of number combinations
 * @param {array} props.topNumbers - Array of top numbers
 * @param {array} props.coldNumbers - Array of cold numbers
 * @param {object} props.lastCombination - Last extracted combination
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message if any
 * @param {object} props.additionalData - Additional data (AI predictions, winners, etc.)
 * @param {string} props.title - Title for the statistics section
 * @param {boolean} props.showColdNumbers - Whether to display cold numbers
 */
const LotteryStatsDisplay = ({
  combinations = [],
  topNumbers = [],
  coldNumbers = [],
  lastCombination = null,
  loading = false,
  error = null,
  additionalData = {},
  title = 'Statistics',
  showColdNumbers = false,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="alert alert-info">
        <div className="spinner-border spinner-border-sm me-2" role="status">
          <span className="visually-hidden">{t('loading')}</span>
        </div>
        {t('loading')}...
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <strong>{t('error')}:</strong> {error}
      </div>
    );
  }

  // Filter out empty additional data
  const filteredAdditionalData = Object.entries(additionalData)
    .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    .reduce((obj, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {});

  return (
    <div>
      {/* Last Combination Section */}
      {lastCombination && (
        <div className="mb-2">
          <div className="card card-last-combination">
            <div className="card-body">
              <h5 className="card-title">
                <i className="icon-last">🎰</i> {t('lastExtractedCombination')}
              </h5>
              <div className="last-combination-content">
                <div className="combination-display">
                  {(Array.isArray(lastCombination.combination)
                    ? lastCombination.combination
                    : String(lastCombination.combination).split(' ')
                  ).map((num, idx) => {
                    const numValue = Number(num);
                    const jollyValue = lastCombination.jolly ? Number(lastCombination.jolly) : null;
                    const superstarValue = lastCombination.superstar ? Number(lastCombination.superstar) : null;

                    let numberClass = 'number-last';
                    let badge = null;

                    // Identify Jolly (yellow) and SuperStar (green)
                    if (jollyValue !== null && numValue === jollyValue) {
                      numberClass = 'number-jolly';
                      badge = t('jolly');
                    } else if (superstarValue !== null && numValue === superstarValue) {
                      numberClass = 'number-superstar';
                      badge = t('superstar');
                    }

                    return (
                      <div key={idx} className="number-with-badge">
                        <div className={`combination-number ${numberClass}`}>
                          {num}
                        </div>
                        {badge && <span className={`number-badge-label badge-${badge.toLowerCase()}`}>{badge}</span>}
                      </div>
                    );
                  })}
                </div>
                <div className="last-combination-info">
                  <div className="info-item">
                    <span className="info-label">{t('competitionNumber')}:</span>
                    <span className="info-value">#{lastCombination.competitionNumber}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">{t('of')}:</span>
                    <span className="info-value">{lastCombination.date}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Numbers Section */}
      {topNumbers.length > 0 && (
        <div className="mb-2">
          <div className="card card-top-numbers">
            <div className="card-body">
              <h5 className="card-title">
                <i className="icon-top">⭐</i> {t('topNumbers')}
              </h5>
              <div className="combination-display">
                {topNumbers.map((num, idx) => (
                  <div key={idx} className="combination-number number-top">
                    {num}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cold Numbers Section */}
      {showColdNumbers && coldNumbers.length > 0 && (
        <div className="mb-2">
          <div className="card card-cold-numbers">
            <div className="card-body">
              <h5 className="card-title">
                <i className="icon-cold">❄️</i> {t('coldNumbers')}
              </h5>
              <div className="combination-display">
                {coldNumbers.map((num, idx) => (
                  <div key={idx} className="combination-number number-cold">
                    {num}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Prediction Section (Enhanced Display) */}
      {filteredAdditionalData.predictAi && (
        <div className="mb-2">
          <div className="card card-ai-prediction">
            <div className="card-body">
              <div className="ai-prediction">
                <h4 className="ai-prediction-title">
                  <i className="icon-ai">🤖</i> {t('aiPrediction') || 'Pronostico AI'}
                </h4>
                <div className="ai-prediction-display">
                  <div className="combination-display">
                    {filteredAdditionalData.predictAi.split(' ').map((num, idx) => (
                      <div key={idx} className="combination-number number-ai">
                        {num}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional Data Section */}
      {Object.keys(filteredAdditionalData).length > 0 && (
        <div className="mb-2">
          <div className="card card-additional-data">
            <div className="card-body">
              <h5 className="card-title">
                <i className="info-icon">ℹ</i> {t('additionalData')}
              </h5>
              <div className="additional-data-grid">
                {Object.entries(filteredAdditionalData)
                  .filter(([key]) => key !== 'predictAi') // Exclude predictAi as it's now displayed separately
                  .map(([key, value]) => (
                    <div key={key} className="additional-data-item">
                      <div className="data-label">{t(key) || key}:</div>
                      <div className="data-value">{value}</div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Probable Combinations Section */}
      {combinations.length > 0 ? (
        <div className="mb-2">
          <div className="card card-combinations">
            <div className="card-body">
              <h5 className="card-title">
                <i className="combo-icon">🎯</i> {t('probableCombinations')}
              </h5>
              <div className="combinations-grid">
                {combinations.map((combination, index) => (
                  <div key={index} className="combination-row">
                    <span className="combination-index">{index + 1}</span>
                    <span className="combination-numbers">
                      {Array.isArray(combination)
                        ? combination.join(' | ')
                        : combination}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="alert alert-info">{t('noCombinationsFound')}</div>
      )}
    </div>
  );
};

export default LotteryStatsDisplay;
